import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import FilmTimes from "./FilmTimes";
import { Link } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { Helmet } from "react-helmet-async";
import { ContentWrapper } from "../loading/LoadingSkeleton";
import { API_URL } from "../../config";

// ProgrammaFilmCard component for individual film animations
function ProgrammaFilmCard({ programmafilm, isMobile }) {
	const [imageLoaded, setImageLoaded] = useState(false);
	const [imageSrc, setImageSrc] = useState('');

	useEffect(() => {
		const thumbnail = programmafilm.attributes.still?.data?.attributes?.formats?.thumbnail?.url;
		const medium = programmafilm.attributes.still?.data?.attributes?.formats?.medium?.url;
		
		if (thumbnail) {
			setImageSrc(thumbnail);
			if (medium && medium !== thumbnail) {
				const img = new Image();
				img.onload = () => setImageSrc(medium);
				img.src = medium;
			}
		} else if (medium) {
			setImageSrc(medium);
		}
	}, [programmafilm]);

	return (
		<div 
			className="progfilm-container"
			style={{
				opacity: imageLoaded ? 1 : 0,
				transition: 'opacity 0.2s ease-in-out'
			}}
		>
			<img
				className='progfilm-banner-image'
				src={imageSrc}
				alt={"Filmstill van " + programmafilm.attributes.titel}
				loading="lazy"
				onLoad={() => setImageLoaded(true)}
			/>
			<div className='film-details'>
				<Link to={"/films/" + programmafilm.attributes.productionid}>
					<h2 className='film-titel' key={programmafilm.attributes.titel}>
						{programmafilm.attributes.titel}
					</h2>
				</Link>
				<p className='film-subtekst'>
					{programmafilm.attributes.regie} - {programmafilm.attributes.jaar} - {programmafilm.attributes.speelduur}
					{isMobile && <br />}
					{!isMobile && " - "}
					{programmafilm.attributes.gesproken} - {programmafilm.attributes.ondertitels}
				</p>
			</div>
			<FilmTimes slug={programmafilm.attributes.productionid} />
			{programmafilm.attributes.beschrijving && JSON.parse(programmafilm.attributes.beschrijving).blocks.map(
				(block) => (
					<p
						className='tekst'
						key={block.id}
						dangerouslySetInnerHTML={{
							__html: block.data.text,
						}}
					/>
				)
			)}
		</div>
	);
}

export default function Programma() {
	const { id } = useParams();
	const [programma, setProgramma] = useState(false);
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(true);
	const [mainImageLoaded, setMainImageLoaded] = useState(false);
	const isMobile = useMediaQuery({ query: "(max-width: 576px)" });

	useEffect(() => {
		const fetchData = async () => {
			try {
				// Check cache first
				const cacheKey = `kriterion-programma-${id}`;
				const cacheTimeKey = `kriterion-programma-${id}-time`;
				const cachedData = localStorage.getItem(cacheKey);
				const cacheTime = localStorage.getItem(cacheTimeKey);
				
				if (cachedData && cacheTime && (Date.now() - parseInt(cacheTime)) < 300000) {
					const parsed = JSON.parse(cachedData);
					setProgramma(parsed);
					setLoading(false);
					return;
				}

				const response = await axios.get(`${API_URL}/api/programmas/${id}?populate=*`);
				
				const programmaData = response.data.data;
				localStorage.setItem(cacheKey, JSON.stringify(programmaData));
				localStorage.setItem(cacheTimeKey, Date.now().toString());
				
				setError("");
				setProgramma(programmaData);
				setLoading(false);
			} catch (e) {
				if (e.response?.status === 404) {
					setProgramma(false);
					setError("Title not found");
				} else {
					setError("Something went wrong, try again later");
				}
				setLoading(false);
			}
		};

		fetchData();
		window.scrollTo(0, 0);
	}, [id]);

	return (
		<ContentWrapper isLoading={loading && !error} loadingMessage="Programma wordt geladen...">
			<Helmet>
				<title>{programma ? `${programma.attributes.title} - Filmprogramma - Filmtheater Kriterion` : 'Filmprogramma - Filmtheater Kriterion'}</title>
				<meta name="description" content={programma ? `${programma.attributes.title} - Filmprogramma in Filmtheater Kriterion Amsterdam. Bekijk tijden en bestel tickets.` : 'Bekijk dit filmprogramma in Filmtheater Kriterion Amsterdam.'} />
			</Helmet>
			<div className="body-container">
				{programma && (
					<div 
						className="programma-content"
						style={{
							opacity: mainImageLoaded ? 1 : 0,
							transition: 'opacity 0.2s ease-in-out'
						}}
					>
						<img
							className='banner-image'
							src={programma.attributes.cover?.data?.attributes?.formats?.thumbnail?.url || programma.attributes.cover?.data?.attributes?.formats?.medium?.url}
							alt={"Foto van Programma " + programma.attributes.title}
							loading="lazy"
							onLoad={() => {
								setMainImageLoaded(true);
								// Upgrade to medium image
								const mediumUrl = programma.attributes.cover?.data?.attributes?.formats?.medium?.url;
								if (mediumUrl) {
									const img = new Image();
									img.onload = () => {
										const bannerImg = document.querySelector('.banner-image');
										if (bannerImg) bannerImg.src = mediumUrl;
									};
									img.src = mediumUrl;
								}
							}}
						/>
						<div className='programma-details'>
							<h2
								className='film-titel'
								key={programma.attributes.title}
							>
								{programma.attributes.title}
							</h2>
							{ }
							{/* Loop over films, display title + year (and link) */}
							{/* Programma description */}
							{programma.attributes.films.data.map(
								(programmafilm) => (
									<Link
										key={programmafilm.attributes.productionid}
										to={
											"/films/" +
											programmafilm.attributes.productionid
										}
									>
										<p className='programma-subtekst'>
											{programmafilm.attributes.titel +
												" (" +
												programmafilm.attributes.jaar +
												")"}
										</p>
									</Link>
								)
							)}
						</div>
						{programma.attributes.description && JSON.parse(programma.attributes.description).blocks.map(
							(block) => (
								<p
									className='tekst'
									key={block.id}
									dangerouslySetInnerHTML={{
										__html: block.data.text,
									}}
								/>
							)
						)}
						{programma.attributes.films.data.map((programmafilm) => (
							<ProgrammaFilmCard 
								key={programmafilm.attributes.productionid} 
								programmafilm={programmafilm} 
								isMobile={isMobile} 
							/>
						))}

					</div>
				)}
				{error && <p className='error'>{error}</p>}
			</div>
		</ContentWrapper>
	);
}

// function isOndertitel(taal) {
// 	if (taal === "Nederlands") {
// 		return "NL";
// 	} else if (!taal || taal === "Geen"){
// 		return "Geen";
// 	} else {
// 		return taal + "e";
// 	}
// }

// function isGesproken(taal) {
// 	if (taal === "Nederlands") {
// 		return "NL";
// 	} else if (!taal || taal === "Geen"){
// 		return "Niet";
// 	} else {
// 		return taal;
// 	}
// }
